import {Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {AuthorizationService} from '../../authorization/authorization.service';
import {MobileViewService} from 'gelios-ng-admin-components';
import {Router, RoutesRecognized} from '@angular/router';
import {NavigationService} from './navigation.service';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {debounceTime, map} from 'rxjs/operators';
import {Menu, MenuChildModel, MenuInstant, MenuItem, MenuItemConfig} from './menu-child.model';

interface RouteModel {
  children: Array<MenuChildModel>;
  config: MenuItemConfig ;
}

@Component({
  selector: 'app-main-navigation',
  templateUrl: './main-navigation.component.html',
  styleUrls: ['./main-navigation.component.scss']
})
export class MainNavigationComponent implements OnInit {
  @ViewChild('asideContainer') asideContainer: ElementRef;
  @Input() links: { [key: string]: MenuItem };
  linksInstant: Observable<MenuInstant>;
  availableRoutes: BehaviorSubject<Array<[string, RouteModel]>> = new BehaviorSubject(null);
  activeMenuTabIndex = null;

  constructor(
    public authorizationService: AuthorizationService,
    public mobileView: MobileViewService,
    public router: Router,
    public navigationService: NavigationService,
  ) {
    if (this.mobileView.mobileViewEnabled) {
      this.navigationService.openMenu.subscribe(menuVisibility => {
        if (menuVisibility) {
          const mainRoutePath = this.router.url;
          const currentMainRouteIndex = this.availableMainRouteIndex(mainRoutePath);
          if (!this.menuLinkIsSingle(mainRoutePath)) {
            this.activeMenuTabIndex = currentMainRouteIndex;
          }
        } else {
          this.activeMenuTabIndex = null;
        }
      });
    }
  }

  get hiddenMenu() {
    return !!localStorage.getItem('hideMenu');
  }
  getMainRouterUrl(url: string) {
    return url.split('/').filter(part => part)[0];
  }

  hideChildMenuItems(item: RouteModel) {
    return item.children.length === 1 && item.config.title === item.children[0].name;
  }

  toggleDropDownMenu(item: [string, RouteModel], index: number) {
    if (this.hideChildMenuItems(item[1])) {
      this.activeMenuTabIndex = null;
      this.router.navigate([item[1].children[0].url]);
      this.closeMenuDropdown();
    } else {
      if (item[1].children.length === 1) {
        this.router.navigate([item[1].children[0].url]);
      }
      if (this.hiddenMenu) {
        this.activeMenuTabIndex = index;
        localStorage.removeItem('hideMenu');
      } else {
        this.activeMenuTabIndex = this.activeMenuTabIndex !== index ? index : null;
      }
    }
  }

  closeMenuDropdown() {
    if (this.mobileView.mobileViewEnabled) {
      this.navigationService.openMenu.next(false);
      this.activeMenuTabIndex = null;
    }
  }

  toggleMenu() {
    if (this.hiddenMenu) {
      localStorage.removeItem('hideMenu');
      this.openDropdownMenu();
    } else {
      localStorage.setItem('hideMenu', 'yes');
      this.activeMenuTabIndex = null;
    }
  }

  menuLinkIsSingle(mainRoutePath: string) {
    const routeKey = this.findRouteKey(this.availableRoutes.getValue(), mainRoutePath);
    return routeKey && this.availableRoutes.getValue()[routeKey]
      ? this.availableRoutes.getValue()[routeKey].children.length === 1 : null;
  }

  availableMainRouteIndex(mainRoutePath: string) {
    const routeKey = this.findRouteKey(this.availableRoutes.getValue(), mainRoutePath);
    return routeKey ? this.availableRoutes.getValue().findIndex(route => route[0] === routeKey) : -1;
  }

  openDropdownMenu() {
    this.activeMenuTabIndex = this.availableMainRouteIndex(this.router.url);
  }

  includedCurrentMainRouterUrl(item: RouteModel) {
    return item.children.filter(e => this.getMainRouterUrl(e.url) === this.getMainRouterUrl(this.router.url)).length > 0;
  }

  mainLinkIsActive(item: [string, RouteModel], index: number) {
    return this.includedCurrentMainRouterUrl(item[1])
      && (this.activeMenuTabIndex !== index || this.hideChildMenuItems(item[1]));
  }

  ngOnInit() {
    this.linksInstant = combineLatest(
      of(this.links),
      ...(
        Object.entries(this.links)
          .map(
            ([level1Key, level1MenuItem]) => {
              return level1MenuItem.children.map(
                c => ({
                  key: level1Key,
                  config:  level1MenuItem.config,
                  item: c
                })
              );
            }
          )
          .reduce(
            (a, c) => a.concat(c),
            []
          )
          .map(
            r => r.item.visible
          )
      )
    ).pipe(
      debounceTime(300),
      map(
        result => {
          const links = result[0] as Menu;
          let i = 1;
          const linksInstant: MenuInstant = Object.entries(links).reduce(
            (acc, [key, item]) => {
              acc[key] = {
                config: item.config,
                children: item.children.map(
                  ch => {
                    const chInstant = {
                      name: ch.name,
                      url: ch.url,
                      menu: ch.menu,
                      title: ch.title,
                      visible: result[i],
                    };
                    i++;
                    return chInstant;
                  }
                )
              };
              return acc;
            },
            {}
          );
          return linksInstant;
        }
      )
    );
    this.linksInstant.pipe(
      map(
        links => {
          const mainMenu = this.authorizationService.identity.value.access_rights.mainMenu as Array<string>;
          const mainMenuIndexed: {[menuItemName: string]: boolean} = mainMenu.reduce(
            (acc, item) => { acc[item] = true; return acc; },
            {}
          );
          const availableChildrenRoutes = {};
          Object.keys(links).forEach(mainLink => {
            if (links[mainLink].children.length > 0) {
              const children = [];
              links[mainLink].children.forEach(item => {
                if ((item.menu in mainMenuIndexed) && item.visible) {
                  children.push(item);
                }
              });
              if (children.length > 0) {
                availableChildrenRoutes[mainLink] = {
                  children: children,
                  config: this.links[mainLink].config
                };
              }
            }
          });
          return Object.entries(availableChildrenRoutes);
        }
      )
    ).subscribe(
      result => {
        this.availableRoutes.next(result as Array<[string, RouteModel]>);
        if (!this.hiddenMenu) {
          this.openDropdownMenu();
        }
      }
    );
    // this.availableRoutes.next(this.availableMainRoutes());
    this.router.events.subscribe(
      event => {
        if (event instanceof RoutesRecognized) {
          const currentUrl = this.getMainRouterUrl(this.router.url);
          const urlAfterRedirects = this.getMainRouterUrl(event.urlAfterRedirects);
          if (!this.hiddenMenu && currentUrl !== urlAfterRedirects) {
            this.activeMenuTabIndex = this.availableMainRouteIndex(urlAfterRedirects);
          }
        }
      }
    );
  }

  findRouteKey(routes: Array<[string, RouteModel]>, url: string): string | null {
    const mainRouterUrl = this.getMainRouterUrl(url);
    for (const item of routes) {
      if (item[1].children.some((child: MenuChildModel) => this.getMainRouterUrl(child.url) === mainRouterUrl)) {
        return item[0];
      }
    }
    return null;
  }
}
